import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch from '../../hooks/useFetch';
import { Page, PageTitle, DataTable, SelectLocation } from '../../components';
import InlineLoader from '../../loading/InlineLoader';
import { Checkbox, Select, MenuItem, FormControl, InputLabel, Grid, Typography, Alert } from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { STAFF, LOCATIONS } from '../../constants';
import { dateLocaleFor } from '../../i18n/formats';
import { tokens } from '../../theme'

export default function ConfirmSales(props) {
  const { setAuth } = props;
  const { isPending, fetchResource, showAlert, alertMessage, setAlertMessage, setShowAlert } = useFetch({ setAuth });
  const { t, i18n } = useTranslation();
  const [location, setLocation] = useState('');
  const [showChangeLocation, setShowChangeLocation] = useState(false);
  const [sales, setSales] = useState([]);

  const desktopColumns = [
    {
      field: 'dateProcessed',
      headerName: t('commissions.columns.date'),
      width: 100,
      valueGetter: params => new Date(params.value),
      renderCell: params => new Intl.DateTimeFormat(dateLocaleFor(i18n.language)).format(new Date(params.value))
    },
    { field: 'title', headerName: t('commissions.columns.description'), width: 120, flex: 1 },
    { field: 'customerName', headerName: t('commissions.columns.customer'), width: 100, },
    { field: 'price', headerName: t('commissions.columns.price'), minWidth: 80, valueFormatter: params => `$${params.value}` },
    { field: 'discount', headerName: t('commissions.columns.discount'), minWidth: 80, valueFormatter: params => `$${params.value}` },
    { field: 'tax', headerName: t('commissions.columns.tax'), minWidth: 80, valueFormatter: params => `$${params.value}` },
    {
      field: 'comPayable',
      headerName: t('commissions.columns.payable'),
      minWidth: 100,
      valueFormatter: params => `$${params.value}`,
      renderCell: (params) =><span style={{ fontWeight: 'bold' }}>${params.value}</span>
    },
    { field: 'attributedStaff', headerName: t('commissions.columns.salesperson'), minWidth: 150, renderCell: params => StaffSelectionDropdown(params) },
    { field: 'comEligible', headerName: t('commissions.columns.eligible'), width: 100, cellClassName: 'right-align', renderCell: params => ComEligibleCheckbox(params) }
  ]

  function saveLocation(locationToSet) {
    localStorage.setItem('location', locationToSet);
    setLocation(locationToSet);
    setShowChangeLocation(false);
  }

  function handleCheckboxChange(id, checked) {
    setSales(prev =>
      prev.map(row => row.id === id ? { ...row, comEligible: checked ? 1 : 0 } : row)
    );
  };

  function handleSelectChange(id, value) {
    setSales(prev =>
      prev.map(row => row.id === id ? { ...row, attributedStaff: value } : row)
    );
  }

  function handleRowClick(id) {
    setSales(prev =>
      prev.map(row => row.id === id[0] ? { ...row, comEligible: !row.comEligible } : row)
    );
  };

  async function handleConfirmCommissions() {
    if (!sales.length) return;

    for (const sale of sales) {
      if (sale.comEligible && !sale.attributedStaff) {
        setAlertMessage({ message: t('commissions.confirmSales.missingSalesperson', { title: sale.title }), type: 'error' });
        setShowAlert(true);
        return;
      }
    }

    try {
      const response = await fetchResource({
        resource: 'commissions/confirm-commissions',
        body: { sales: sales },
        blocking: true
      });
      if (response.result !== 'OK') throw new Error(response.error);
      setAlertMessage({ message: t('commissions.confirmSuccess'), type: 'success' });
      setShowAlert(true);
      setSales([]);
    } catch (error) {
      setAlertMessage({ message: error.message, type: 'error' });
      setShowAlert(true);
      console.error(error);
    }
  }

  async function getSales() {
    try {
      const response = await fetchResource({ resource: 'commissions/unconfirmed-com-sales', body: { location: location }, loadingKey: 'sales' });
      if (!response.sales) throw new Error(response.error);
      setSales(response.sales);
    } catch (error) {
      setSales([]);
      console.error(error);
    }
  }

  useEffect(() => {
    if (!location) return;
    localStorage.setItem('location', location);
    getSales();
  }, [location]);

  useEffect(() => {
    const storedLocation = localStorage.getItem('location');
    if (!storedLocation) return setShowChangeLocation(true);
    setLocation(storedLocation);
  }, []);

  const Header = () => (
    <Grid container position='relative'>
        <Grid container position='absolute' mt={1} justifyContent='space-between'>
          <Grid item>
            <LocationOnIcon
                onClick={() => setShowChangeLocation(true)}
                sx={{ cursor: 'pointer', margin: '5px' }}
            />
          </Grid>
          <Grid item>
            <TaskAltIcon
                onClick={handleConfirmCommissions}
                sx={{ cursor: 'pointer', margin: '5px' }}
            />
          </Grid>
        </Grid>
        <Grid item width='100%'>
            <PageTitle>{t('commissions.confirmSales.title')} - <b>{location && LOCATIONS[location]}</b></PageTitle>
        </Grid>
    </Grid>
  )

  const ChangeLocationPopup = () => (
    <SelectLocation
        location={location}
        saveLocation={saveLocation}
        showChangeLocation={showChangeLocation}
        setShowChangeLocation={setShowChangeLocation}
    />
  )
  
  const StaffSelectionDropdown = (params) => (
    <FormControl fullWidth>
      <InputLabel id="associated-staff-label">{t('commissions.columns.salesperson')}</InputLabel>
      <Select
          fullWidth
          labelId="associated-staff-label"
          id="associated-staff"
          value={params.value || ''}
          label={t('commissions.columns.salesperson')}
          onChange={e => handleSelectChange(params.id, e.target.value)}
      >
        {Object.keys(STAFF[location]).map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
      </Select>
    </FormControl>
  )

  const ComEligibleCheckbox = (params) => (
    <Checkbox
      checked={!!params.value}
      onChange={e => handleCheckboxChange(params.id, e.target.checked)}
    />
  )

  const SalesTable = () => {
    // Eligible is a status, which is the one job green still has here.
    const eligible = tokens.successSoft;

    return (
      <DataTable
        id="sales-table"
        rowHeight={100}
        loading={isPending('sales')}
        desktopColumns={desktopColumns}
        rows={sales}
        handleSelectChange={handleRowClick}
        getRowClassName={params => params.row.comEligible ? 'eligible' : ''}
        sortModel={[
          { field: 'dateProcessed', sort: 'asc' }
        ]}
        pageSize={100}
        rowsPerPageOptions={[100]}
        sx={{
          '.eligible': {
            backgroundColor: `${eligible} !important`,
            '&:hover': { backgroundColor: `${eligible} !important` },
            '&.Mui-selected': { backgroundColor: `${eligible}` },
            '&.Mui-selected:hover': { backgroundColor: `${eligible}` },
          },
          '.MuiDataGrid-row': {
            cursor: 'pointer',

            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&.Mui-selected': {
              backgroundColor: 'transparent',
            },
            '&.Mui-selected:hover': {
              backgroundColor: 'transparent',
            },
          }
        }}
      />
    )
  }

  const AllConfirmed = () => {
    if (isPending('sales')) return <InlineLoader />;
    return (
      <Grid container justifyContent='center' alignItems='center' height='100%'>
        <Typography variant='h6'>{t('commissions.confirmSales.nonePending')}</Typography>
      </Grid>
    )
  }

  const AlertMessage = () => <Alert className='alert' severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>
  
  return (<>
    <Page>
      {showAlert && AlertMessage()}
      {Header()}
      {showChangeLocation && ChangeLocationPopup()}
      {sales.length ? SalesTable() : AllConfirmed()}
    </Page>
  </>)
}